import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Workflow } from '../services/workflowService';
import Footer from 'components/PageFooter';
import { ClientIntegrationList } from 'services/clientIntegrationService';
import { runWorkflow } from 'services/workflowOperationService';
import { filePreviewKey } from 'components/ImageUpload';

interface ProcessingStep {
  id: string;
  name: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  message?: string;
}

interface LocationState {
  files: File[];
  fileGroups?: File[][];
  slotIds?: string[];
  imagePreviews: { [key: string]: string };
  validationPassed: boolean;
  selectedWorkflow: Workflow;
  platforms: ClientIntegrationList;
}

const ImageProcessingResults: React.FC = () => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { files, fileGroups, slotIds, imagePreviews, selectedWorkflow } = location.state || {};

  const previewEntries = React.useMemo(() => {
    if (fileGroups?.length && slotIds?.length === fileGroups.length) {
      return fileGroups.flatMap((group, gi) =>
        group.map(file => ({ file, previewKey: filePreviewKey(slotIds[gi], file) }))
      );
    }
    return (files || []).map(file => ({ file, previewKey: file.name }));
  }, [fileGroups, slotIds, files]);

  const [steps, setSteps] = useState<ProcessingStep[]>([
    { id: 'validation', name: 'File Size Validation', status: 'completed' },
    { id: 'analysis', name: 'Workflow Execution', status: 'pending' },
    {
      id: 'backendProcessing',
      name: 'Server',
      status: 'pending',
      message: 'Waiting for confirmation from the server…',
    },
  ]);

  const [error, setError] = useState<string | null>(null);
  const usageId: number | null = null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const updateStepStatus = React.useCallback((stepId: string, status: ProcessingStep['status'], message?: string) => {
    setSteps(prevSteps =>
      prevSteps.map(step => (step.id === stepId ? { ...step, status, message } : step))
    );
  }, []);

  const processWorkflow = React.useCallback(async () => {
    if (!selectedWorkflow || !files?.length) return;

    try {
      setError(null);
      updateStepStatus('analysis', 'processing', `Sending to server: ${selectedWorkflow.name}...`);

      const response = await runWorkflow(
        selectedWorkflow.id,
        files,
        fileGroups != null && fileGroups.length > 0 ? { fileGroups } : undefined
      );

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `Server error (${response.status})`);
      }

      try {
        await response.text();
      } catch {
      }

      updateStepStatus('analysis', 'completed', 'Upload finished — your files were sent successfully.');
      updateStepStatus(
        'backendProcessing',
        'completed',
        'Items are being processed on the server. You can leave this page; processing continues in the background.'
      );
    } catch (err) {
      console.error('Workflow execution error:', err);
      const msg = err instanceof Error ? err.message : 'Failed to execute workflow. Please try again.';
      updateStepStatus('analysis', 'failed', msg);
      updateStepStatus('backendProcessing', 'skipped', 'No server confirmation — upload did not complete.');
      setError(msg);
    }
  }, [selectedWorkflow, files, fileGroups, updateStepStatus]);

  useEffect(() => {
    if (!files || files.length === 0 || !imagePreviews || !selectedWorkflow) {
      history.push('/image-upload');
      return;
    }

    processWorkflow();
  }, [files, imagePreviews, selectedWorkflow, history, processWorkflow]);

  const getStepIcon = (status: ProcessingStep['status']) => {
    switch (status) {
      case 'completed':
        return (
          <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'processing':
        return (
          <svg className="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'failed':
        return (
          <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'skipped':
        return (
          <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="h-6 w-6 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const getStepColor = (status: ProcessingStep['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-700';
      case 'processing':
        return 'bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-700';
      case 'failed':
        return 'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-700';
      case 'skipped':
        return 'bg-yellow-50 dark:bg-yellow-900 border-yellow-200 dark:border-yellow-700';
      default:
        return 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600';
    }
  };

  if (!files || files.length === 0 || !imagePreviews) {
    return null;
  }

  const totalPreviews = previewEntries.length;
  const analysisStep = steps.find(s => s.id === 'analysis');
  const backendStep = steps.find(s => s.id === 'backendProcessing');
  const headerSubtitle =
    backendStep?.status === 'completed'
      ? 'All set — items are being processed.'
      : analysisStep?.status === 'failed'
        ? 'Something went wrong — see details below.'
        : 'Sending files to the server…';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <div className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-12">
        <div className="mb-6">
          <button
            onClick={() => history.push('/image-upload')}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
          >
            <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Upload
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Image Processing Results</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">{headerSubtitle}</p>
        </div>

        <div className="card mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Processing Pipeline</h2>

          <div className="space-y-4">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`p-4 rounded-lg border ${getStepColor(step.status)}`}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {getStepIcon(step.status)}
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      {index + 1}. {step.name}
                    </h3>
                    {step.message && (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                        {step.message}
                      </p>
                    )}
                    {step.status === 'processing' && (
                      <p className="mt-2 text-sm text-blue-600 dark:text-blue-400 font-medium animate-pulse">
                        Processing...
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div className="mt-6 p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg">
              <div className="flex">
                <svg className="h-5 w-5 text-red-600 dark:text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Processing Error</h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {selectedWorkflow && (
            <div className="mt-6 p-6 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-4">Selected Workflow</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white dark:bg-blue-800 rounded-md">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Workflow Name:</span>
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">{selectedWorkflow.name}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white dark:bg-blue-800 rounded-md">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Type:</span>
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-100 capitalize">{selectedWorkflow.workflow_type}</span>
                </div>
                {usageId != null && (
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-blue-800 rounded-md">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Tracking ID:</span>
                    <span className="text-sm font-mono text-gray-700 dark:text-gray-200">#{usageId}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="card mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Uploaded Image{totalPreviews > 1 ? 's' : ''} ({totalPreviews})
          </h2>
          <div className={`grid ${totalPreviews > 1 ? 'grid-cols-2 gap-4' : 'grid-cols-1'}`}>
            {previewEntries.map(({ file, previewKey }, index) => (
              <div key={`${previewKey}-${index}`} className="flex flex-col items-center">
                <img
                  src={imagePreviews[previewKey]}
                  alt={`Upload ${index + 1}`}
                  className="max-w-[60%] max-h-56 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
                />
                <div className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                  <p className="font-medium">{file.name}</p>
                  <p>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ImageProcessingResults;
